import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, Save, X, AlertCircle, Package, CheckCircle2, ChevronLeft, ChevronRight,
  Layers, Cpu, Globe, Shield, Mail, Server, SlidersHorizontal, Sparkles,
} from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useConfirm } from '../../components/ConfirmDialog';
import { billingApi } from '../../api/billing';
import type { ProductType } from '../../api/billing';
import { customerPortalApi, type PortalProduct } from '../../api/customerPortal';
import EntityDetailModal from '../../components/EntityDetailModal';
import { cardOpen } from '../../utils/cardOpen';
import { timeAgoWithDate } from '../../utils/formatTime';

const EMPTY = {
  name: '', description: '', kind: 'product', sku: '', price: '', currency: 'USD',
  billing_interval: '', module_key: '', stock: '', is_active: true, stripe_price_id: '',
};
type FormState = typeof EMPTY;

const TYPE_ICON: Record<string, any> = {
  plan: Layers, memory: Cpu, domain: Globe, ssl: Shield, email: Mail,
  hosting: Server, security: Shield, bundle: Package, custom: SlidersHorizontal,
};

export default function ProductsApp() {
  const [products, setProducts] = useState<PortalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  // Type-first multi-step selector (Phase H): hidden behind the "New Product" button.
  const [types, setTypes] = useState<ProductType[]>([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [chosenType, setChosenType] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [detailProduct, setDetailProduct] = useState<PortalProduct | null>(null);

  const load = async () => {
    setLoading(true);
    try { setProducts(await customerPortalApi.listProducts()); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Could not load products.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => { billingApi.getProductTypes().then(d => setTypes(d.types)).catch(() => setTypes([])); }, []);

  const core = useMemo(() => types.filter(t => t.group === 'core'), [types]);
  const additional = useMemo(() => types.filter(t => t.group === 'additional'), [types]);
  const activeType = useMemo(() => types.find(t => t.key === chosenType) || null, [types, chosenType]);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setStep(0); setChosenType(''); setOpen(true); setError(''); setSuccess(''); };
  const openEdit = (p: PortalProduct) => {
    setForm({
      name: p.name, description: p.description || '', kind: p.kind, sku: p.sku || '',
      price: (p.price_cents / 100).toFixed(2), currency: p.currency,
      billing_interval: p.billing_interval || '', module_key: p.module_key || '',
      stock: p.stock == null ? '' : String(p.stock), is_active: p.is_active,
      stripe_price_id: p.stripe_price_id || '',
    });
    setEditingId(p.id); setChosenType(''); setStep(1); setOpen(true); setError(''); setSuccess('');
  };

  const chooseType = (key: string) => {
    setChosenType(key);
    const t = types.find(x => x.key === key);
    // Prefill sensible defaults from the type.
    setForm(prev => ({
      ...prev,
      kind: key === 'plan' ? 'plan' : (key === 'bundle' ? 'bundle' : 'product'),
      billing_interval: key === 'domain' ? 'yearly' : (key === 'plan' || key === 'memory' ? 'monthly' : prev.billing_interval),
      name: prev.name || (t ? t.label : ''),
    }));
    setStep(1);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    const price_cents = Math.round(parseFloat(form.price || '0') * 100);
    if (isNaN(price_cents) || price_cents < 0) { setError('Enter a valid price'); setSaving(false); return; }
    const payload: Partial<PortalProduct> = {
      name: form.name.trim(), description: form.description || null,
      kind: form.kind as PortalProduct['kind'], sku: form.sku || null,
      price_cents, currency: form.currency || 'USD',
      billing_interval: (form.billing_interval || null) as PortalProduct['billing_interval'],
      module_key: form.module_key || null,
      stock: form.stock === '' ? null : Number(form.stock),
      is_active: form.is_active, stripe_price_id: form.stripe_price_id || null,
    };
    try {
      if (editingId) await customerPortalApi.updateProduct(editingId, payload);
      else await customerPortalApi.createProduct(payload);
      setSuccess(editingId ? 'Product updated.' : 'Product created.');
      setOpen(false); setEditingId(null);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save product');
    } finally { setSaving(false); }
  };

  const doDelete = async (p: PortalProduct) => {
    const ok = await confirm({
      title: 'Delete product?', message: `"${p.name}" will be permanently removed.`,
      actions: [{ label: 'Cancel', value: false, tone: 'secondary' }, { label: 'Delete', value: 'delete', tone: 'danger' }],
    });
    if (ok === false) return;
    try { await customerPortalApi.deleteProduct(p.id); setSuccess('Product deleted.'); await load(); }
    catch (err: any) { setError(err?.response?.data?.detail || 'Failed to delete product'); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  const typeCard = (t: ProductType) => {
    const Icon = TYPE_ICON[t.key] || Package;
    return (
      <button key={t.key} onClick={() => chooseType(t.key)}
        data-agent-id={`products.type.${t.key}`}
        className="flex flex-col items-start gap-2 rounded-xl border border-slate-200 p-4 text-left transition hover:border-brand-400 hover:shadow-sm dark:border-slate-700">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500/15 to-blue-500/15 text-brand-600 dark:text-brand-300">
          <Icon size={20} />
        </span>
        <span className="font-medium text-slate-950 dark:text-slate-100">{t.label}</span>
        <span className="text-xs text-slate-500">{t.blurb}</span>
        <span className={`mt-1 rounded-full px-2 py-0.5 text-[11px] ${t.options.length ? 'bg-slate-100 text-slate-500 dark:bg-slate-800' : 'bg-transparent text-transparent'}`}>
          {t.options.length ? `${t.options.length} options` : '0 options'}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={18} className="text-slate-500" />
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Products</h2>
            <p className="text-sm text-slate-500">{products.length} product{products.length === 1 ? '' : 's'} tracked.</p>
          </div>
        </div>
        <Button size="sm" onClick={openCreate} data-agent-id="products.new"><Plus size={14} />New Product</Button>
      </div>

      {/* Type-first selector — only shown when the user asks for it (Phase H2). */}
      {open && (
        <Card className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-brand-500" />
              <h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">
                {editingId ? 'Edit product' : (step === 0 ? 'What are you selling?' : `Configure — ${activeType?.label || ''}`)}
              </h3>
            </div>
            <button onClick={() => { setOpen(false); setEditingId(null); }} className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Close"><X size={16} /></button>
          </div>

          {step === 0 && !editingId && (
            <div className="space-y-4">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Our core products</div>
                <div className="grid gap-3 sm:grid-cols-3">{core.map(typeCard)}</div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Additional products</div>
                <div className="grid gap-3 sm:grid-cols-3">{additional.map(typeCard)}</div>
              </div>
            </div>
          )}

          {(step === 1 || editingId) && (
            <>
              {/* Type-specific options (seeded from the live catalog where available). */}
              {activeType && activeType.options.length > 0 && (
                <div>
                  <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">{activeType.label} options (from the live catalog)</div>
                  <select onChange={e => {
                    const opt = activeType.options.find(o => o.key === e.target.value);
                    if (opt) setForm(prev => ({ ...prev, name: opt.label, price: (opt.price_cents / 100).toFixed(2), billing_interval: opt.interval || prev.billing_interval }));
                  }} defaultValue=""
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                    <option value="">Choose a {activeType.label.toLowerCase()} variant…</option>
                    {activeType.options.map(o => (
                      <option key={o.key} value={o.key}>{o.label} — ${(o.price_cents / 100).toFixed(2)}{o.interval ? `/${o.interval === 'yearly' ? 'yr' : 'mo'}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <input placeholder="Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  disabled={!!(activeType && activeType.options.length > 0)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800/50" />
                <input placeholder="SKU" value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                <select value={form.kind} onChange={e => setForm(p => ({ ...p, kind: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                  <option value="product">Product (one-off)</option>
                  <option value="plan">Plan (recurring)</option>
                  <option value="bundle">Bundle</option>
                </select>
                <select value={form.billing_interval} onChange={e => setForm(p => ({ ...p, billing_interval: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                  <option value="">No interval</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
                <input placeholder="Price (USD)" type="number" step="0.01" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                <input placeholder="Stock (blank = unlimited)" type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                <input placeholder="Module key (optional)" value={form.module_key} onChange={e => setForm(p => ({ ...p, module_key: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
                <input placeholder="Stripe price ID (optional)" value={form.stripe_price_id} onChange={e => setForm(p => ({ ...p, stripe_price_id: e.target.value }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
              </div>
              <textarea placeholder="Description" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
                Active
              </label>
              <div className="flex gap-2">
                {!editingId && <Button size="sm" variant="secondary" onClick={() => setStep(0)}><ChevronLeft size={14} />Type</Button>}
                <Button size="sm" onClick={save} disabled={saving || !form.name.trim()}>
                  {saving ? <LoadingSpinner size="sm" /> : <Save size={14} />}Save
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {products.length === 0 && <p className="text-sm text-slate-500">No products yet.</p>}
        {products.map(p => (
          <Card key={p.id} className="cursor-pointer p-4" {...cardOpen({ agentId: `portal.products.card.${p.id}`, label: `Open details for ${p.name}`, onOpen: () => setDetailProduct(p) })}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-950 dark:text-slate-100">{p.name}</div>
                {p.sku && <div className="truncate font-mono text-xs text-slate-400">{p.sku}</div>}
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-brand-500 dark:hover:bg-slate-800" aria-label={`Edit ${p.name}`}><Pencil size={14} /></button>
                <button onClick={(e) => { e.stopPropagation(); void doDelete(p); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-500 dark:hover:bg-slate-800" aria-label={`Delete ${p.name}`}><Trash2 size={14} /></button>
              </div>
            </div>
            {p.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{p.description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 capitalize text-slate-600 dark:bg-slate-800 dark:text-slate-300">{p.kind}</span>
              <span className={`rounded-full px-2 py-0.5 font-medium ${p.is_active ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-slate-500/10 text-slate-500'}`}>{p.is_active ? 'Active' : 'Inactive'}</span>
              {p.billing_interval && <span className="capitalize text-slate-500">{p.billing_interval}</span>}
            </div>
            <div className="mt-3 flex items-baseline justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
              <span className="text-lg font-bold text-slate-950 dark:text-slate-100">${(p.price_cents / 100).toFixed(2)}</span>
              <span className="text-xs text-slate-500">Stock: {p.stock == null ? '∞' : p.stock}</span>
            </div>
          </Card>
        ))}
      </div>

      <EntityDetailModal
        open={!!detailProduct}
        onClose={() => setDetailProduct(null)}
        title={detailProduct ? detailProduct.name : 'Product'}
        rows={detailProduct ? [
          { label: 'Name', value: detailProduct.name },
          { label: 'SKU', value: detailProduct.sku || '—' },
          { label: 'Kind', value: <span className="capitalize">{detailProduct.kind}</span> },
          { label: 'Description', value: detailProduct.description ? <span className="whitespace-pre-wrap break-words">{detailProduct.description}</span> : '—' },
          { label: 'Price', value: `$${(detailProduct.price_cents / 100).toFixed(2)} ${detailProduct.currency}` },
          { label: 'Billing interval', value: detailProduct.billing_interval || '—' },
          { label: 'Module key', value: detailProduct.module_key || '—' },
          { label: 'Stock', value: detailProduct.stock == null ? '∞' : String(detailProduct.stock) },
          { label: 'Active', value: detailProduct.is_active ? 'Yes' : 'No' },
          { label: 'Stripe price ID', value: detailProduct.stripe_price_id || '—' },
          { label: 'Created', value: detailProduct.created_at ? timeAgoWithDate(detailProduct.created_at) : '—' },
        ] : []}
      />
    </div>
  );
}
